/**
 * AuthContext — Google sign-in gate for the console.
 *
 * Access is DB-gated: the Google ID token is verified server-side by the data-api
 * console gate (POST /auth/check/console), which checks the caller's `console`
 * flag in the `auth_users` allowlist (the thema_app DB). Only allowed accounts
 * get in; a verified-but-not-allowlisted user is surfaced as a `waitlist` outcome
 * (the UI shows a request-access dialog) — NOT a hard error.
 *
 * The console is also a PER-USER surface: that same gate returns the WALLET bound
 * to the account (from thema_app), exposed here as `boundWallet`, and the Google
 * ID token is exposed as `idToken` — the bearer credential the per-wallet
 * analytics API requires. A bound wallet is NOT required to sign in (access and
 * having-a-wallet-to-analyze are separate concerns); the connect gate claims or
 * matches it. `bindWallet` claims the connected wallet when none is set yet.
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useVerifyGoogleAuth } from "@thema/ui";
import { AUTH_VERIFY_URL, DATA_API_URL } from "../auth-config";

/**
 * Result of a sign-in attempt. `login` never throws — it resolves to one of:
 *  - `allowed`  : verified + on the console allowlist (session is now active).
 *  - `waitlist` : Google identity verified, but not granted console access yet.
 *  - `error`    : couldn't verify the token / reach the gate.
 */
export type LoginOutcome =
  | { status: "allowed"; email: string }
  | { status: "waitlist"; email: string }
  | { status: "error"; message: string };

interface AuthContextType {
  isAuthenticated: boolean;
  email: string | null;
  /** Lowercased on-chain address bound to this account; the only wallet allowed to connect. */
  boundWallet: string | null;
  /** Google ID token — bearer credential for the authenticated analytics API. */
  idToken: string | null;
  login: (token: string) => Promise<LoginOutcome>;
  logout: () => void;
  /**
   * Claim the connected wallet as this account's wallet (first-connect-wins).
   * Used by the connect gate when the account has no bound wallet yet — the
   * wallet the user connects (their deposit wallet) becomes their bound wallet.
   */
  bindWallet: (address: string) => Promise<void>;
  isLoading: boolean;
}

/** Response shape from POST /auth/check/console (superset of VerifyAuthResponse). */
interface ConsoleAuthResult {
  allowed: boolean;
  email?: string | null;
  wallet?: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_STORAGE_KEY = "thema_console_auth";
const EMAIL_STORAGE_KEY = "thema_console_email";
const WALLET_STORAGE_KEY = "thema_console_wallet";
const TOKEN_STORAGE_KEY = "thema_console_token";

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [boundWallet, setBoundWallet] = useState<string | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { verify, loading: verifying } = useVerifyGoogleAuth();

  // Restore session on mount. The stored ID token may already be expired (Google
  // tokens live ~1h); an expired token simply makes the analytics calls 401 and
  // the user re-signs-in. We still restore it so a fresh reload keeps working.
  useEffect(() => {
    const storedAuth = localStorage.getItem(AUTH_STORAGE_KEY);
    const storedEmail = localStorage.getItem(EMAIL_STORAGE_KEY);
    const storedWallet = localStorage.getItem(WALLET_STORAGE_KEY);
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (storedAuth === "true" && storedEmail) {
      setIsAuthenticated(true);
      setEmail(storedEmail);
      setBoundWallet(storedWallet); // may be null — the connect gate handles that
      setIdToken(storedToken);
    }
    setIsLoading(false);
  }, []);

  const logout = useCallback(() => {
    setIsAuthenticated(false);
    setEmail(null);
    setBoundWallet(null);
    setIdToken(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem(EMAIL_STORAGE_KEY);
    localStorage.removeItem(WALLET_STORAGE_KEY);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    window.google?.accounts?.id?.disableAutoSelect?.();
  }, []);

  const login = useCallback(
    async (token: string): Promise<LoginOutcome> => {
      if (!AUTH_VERIFY_URL) {
        return { status: "error", message: "Auth verify URL is not configured." };
      }

      // `verify` resolves to the gate's JSON ({ allowed, email, wallet }) or null
      // on a network/HTTP failure — it does not throw.
      const result = (await verify(token, AUTH_VERIFY_URL)) as ConsoleAuthResult | null;

      if (!result) {
        logout();
        return { status: "error", message: "Couldn't reach the sign-in service. Please try again." };
      }

      if (result.allowed === true && result.email) {
        // A bound wallet is NOT required to sign in — the connect gate claims or
        // matches it. When absent, boundWallet stays null and the gate prompts.
        const wallet = result.wallet ? result.wallet.toLowerCase() : null;
        setIsAuthenticated(true);
        setEmail(result.email);
        setBoundWallet(wallet);
        setIdToken(token);
        localStorage.setItem(AUTH_STORAGE_KEY, "true");
        localStorage.setItem(EMAIL_STORAGE_KEY, result.email);
        if (wallet) localStorage.setItem(WALLET_STORAGE_KEY, wallet);
        else localStorage.removeItem(WALLET_STORAGE_KEY);
        localStorage.setItem(TOKEN_STORAGE_KEY, token);
        return { status: "allowed", email: result.email };
      }

      // Past here the user is NOT allowed — clear any stale session.
      logout();

      // A verified Google identity (we got an email back) that simply isn't on
      // the console allowlist → waitlist, not an error.
      if (result.email) {
        return { status: "waitlist", email: result.email };
      }

      // allowed=false with no email ⇒ the token couldn't be verified server-side.
      return { status: "error", message: "We couldn't verify your Google sign-in. Please try again." };
    },
    [verify, logout],
  );

  const bindWallet = useCallback(
    async (address: string) => {
      if (!idToken) throw new Error("You must be signed in to bind a wallet.");
      if (!DATA_API_URL) throw new Error("Data API is not configured (set VITE_DATA_API_URL).");
      const res = await fetch(`${DATA_API_URL}/auth/me/wallet`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ wallet: address }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message || `Failed to bind wallet (${res.status}).`);
      }
      const data = (await res.json()) as { wallet: string };
      const w = data.wallet.toLowerCase();
      setBoundWallet(w);
      localStorage.setItem(WALLET_STORAGE_KEY, w);
    },
    [idToken],
  );

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        email,
        boundWallet,
        idToken,
        login,
        logout,
        bindWallet,
        isLoading: isLoading || verifying,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
